import { formatPaise } from '@oneapp/gst';
import type { StatementEntry, StatementGroup, StatementMove, StatementView } from '@oneapp/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShortDate } from '../business/parts';
import { Icon } from '../components/icons';
import { Sheet } from '../components/Sheet';
import { Badge, Button, Callout } from '../components/ui';
import { ApiError } from '../lib/api';
import { parseStatementPath, recordsApi, recordsKeys } from './api';

/**
 * R7 · Every payment: a statement's money in for the month, in its groups (paid for this month's bills,
 * for an earlier bill, walk-in customers, not sales, and the ones asked about), each with its total and
 * what it counts as. Tapping a payment moves it: to walk-in sales, to a bill already in the return, or
 * out of sales. Opened from the chat's "See every payment" (a sheet over it) or on its own from a link.
 */

export type PaymentsFinish = 'done' | 'back';

interface Screen {
  label: string;
  body: ReactNode;
  footer: ReactNode;
}

/** Big groups show this many payments until "Show more". */
const FIRST = 3;

type BadgeTone = 'green' | 'gray' | 'amber' | 'navy';

/** What a group counts as, for its badge. */
function badgeOf(group: StatementGroup): { key: string; tone: BadgeTone } {
  if (group.kind === 'bills' || group.kind === 'walk_in') return { key: 'sales', tone: 'green' };
  if (group.kind === 'earlier') return { key: 'left', tone: 'gray' };
  if (group.status === 'open')
    return group.kind === 'not_sales' ? { key: 'not', tone: 'gray' } : { key: 'check', tone: 'amber' };
  switch (group.decision) {
    case 'sales':
      return { key: 'sales', tone: 'green' };
    case 'some':
      return { key: 'some', tone: 'navy' };
    case 'advance':
      return { key: 'advance', tone: 'gray' };
    case 'add_bill':
      return { key: 'bill', tone: 'navy' };
    default:
      return { key: 'not', tone: 'gray' };
  }
}

/** Out of sales: shown muted, like the canvas's "left out" and "not sales". */
const outOfSales = (group: StatementGroup) =>
  group.kind === 'earlier' ||
  (group.status === 'decided' && (group.decision === 'not_sales' || group.decision === 'advance')) ||
  (group.kind === 'not_sales' && group.status === 'open');

function usePaymentsScreen(importId: string, finish: (how: PaymentsFinish) => void): Screen {
  const { t } = useTranslation();
  const date = useShortDate();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: recordsKeys.statement(importId),
    queryFn: ({ signal }) => recordsApi.statement(importId, signal),
    staleTime: 0,
  });
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [moving, setMoving] = useState<StatementEntry | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [changed, setChanged] = useState(false);

  const view = query.data;
  const month = view ? t(`filings.months.${Number(view.period.slice(5, 7))}`) : '';
  const screen: Screen = {
    label: month ? t('records.payments.label', { month }) : t('sales.loading'),
    body: null,
    footer: (
      <Button onClick={() => finish(changed ? 'done' : 'back')} disabled={busy}>
        {t('records.payments.done')}
      </Button>
    ),
  };
  if (query.isPending) {
    screen.body = <p className="muted">{t('sales.loading')}</p>;
    return screen;
  }
  if (!view) {
    const notFound = query.error instanceof ApiError && query.error.status === 404;
    screen.body = (
      <Callout tone="error" icon="alert-triangle">
        {notFound ? t('records.payments.notFound') : t('records.payments.loadFailed')}
      </Callout>
    );
    return screen;
  }

  const move = async (entry: StatementEntry, to: StatementMove) => {
    setBusy(true);
    setFailed(false);
    try {
      const next: StatementView = await recordsApi.move(importId, entry.id, to);
      queryClient.setQueryData(recordsKeys.statement(importId), next);
      setChanged(true);
      setMoving(null);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const who = (entry: StatementEntry) =>
    entry.payer ??
    (entry.mode === 'cash'
      ? t('records.payments.who.cash')
      : entry.note === 'own_account'
        ? t('records.payments.who.own')
        : entry.note === 'interest'
          ? t('records.payments.who.interest')
          : entry.note === 'loan'
            ? t('records.payments.who.loan')
            : entry.note === 'reversal' || entry.note === 'refund'
              ? t('records.payments.who.refund')
              : t('records.payments.who.unknown'));
  const detail = (entry: StatementEntry) => {
    const parts = [date(entry.date), t(`records.payments.mode.${entry.mode}`)];
    if (entry.bill) {
      parts.push(
        entry.bill.period === view.period
          ? t('records.payments.bill', { number: entry.bill.number })
          : t('records.payments.billEarlier', {
              number: entry.bill.number,
              month: t(`filings.months.${Number(entry.bill.period.slice(5, 7))}`),
            }),
      );
    }
    if (entry.moved) parts.push(t('records.payments.moved'));
    return parts.join(' · ');
  };
  const titleOf = (group: StatementGroup) =>
    group.kind === 'settlement' || (group.kind === 'unmatched' && group.label)
      ? (group.label as string)
      : group.kind === 'not_sales' && group.status === 'open'
        ? t('records.payments.group.probably')
        : t(`records.payments.group.${group.kind}`, { month });
  const credits = view.import.statement?.credits ?? view.entries.length;

  screen.body = (
    <div className="payments">
      <p className="muted">
        {credits === 1 ? t('records.payments.ledeOne') : t('records.payments.lede', { count: credits })}
      </p>
      {view.erased ? <Callout icon="shield-check">{t('records.payments.erased')}</Callout> : null}
      {failed ? (
        <Callout tone="error" icon="alert-triangle" role="alert">
          {t('records.payments.moveFailed')}
        </Callout>
      ) : null}
      {view.groups.map((group) => {
        const entries = view.entries.filter((e) => e.groupId === group.id);
        const shown = open[group.id] ? entries : entries.slice(0, FIRST);
        const badge = badgeOf(group);
        const headId = `payments-${group.id}`;
        return (
          <section key={group.id} className="payments__group" aria-labelledby={headId}>
            <div className="payments__head">
              <span className="payments__titles">
                <h3 className="payments__title" id={headId}>
                  {titleOf(group)}
                </h3>
                <span className="payments__count">
                  {group.count === 1
                    ? t('records.payments.countOne', { total: formatPaise(group.total) })
                    : t('records.payments.count', { count: group.count, total: formatPaise(group.total) })}
                </span>
              </span>
              <Badge tone={badge.tone}>{t(`records.payments.badge.${badge.key}`)}</Badge>
            </div>
            {entries.length ? (
              <ul className="payments__list">
                {shown.map((entry) => (
                  <li key={entry.id}>
                    <button
                      type="button"
                      className={`payments__row${outOfSales(group) ? ' payments__row--out' : ''}`}
                      onClick={() => setMoving(moving?.id === entry.id ? null : entry)}
                      aria-expanded={moving?.id === entry.id}
                      disabled={busy || view.erased}
                    >
                      <span className="payments__text">
                        <span className="payments__who">{who(entry)}</span>
                        <span className="payments__detail">{detail(entry)}</span>
                      </span>
                      <span className="payments__amount">{formatPaise(entry.amount)}</span>
                    </button>
                    {moving?.id === entry.id ? (
                      <MovePanel
                        entry={entry}
                        who={who(entry)}
                        group={group}
                        busy={busy}
                        onMove={(to) => move(entry, to)}
                        onCancel={() => setMoving(null)}
                      />
                    ) : null}
                  </li>
                ))}
                {entries.length > shown.length ? (
                  <li>
                    <button
                      type="button"
                      className="payments__more"
                      onClick={() => setOpen((before) => ({ ...before, [group.id]: true }))}
                    >
                      {t('records.payments.more', { count: entries.length - shown.length })}
                    </button>
                  </li>
                ) : null}
              </ul>
            ) : null}
          </section>
        );
      })}
    </div>
  );
  return screen;
}

/** Where a payment can go: walk-in sales, a bill already in the return, or out of sales. */
function MovePanel({
  entry,
  who,
  group,
  busy,
  onMove,
  onCancel,
}: {
  entry: StatementEntry;
  who: string;
  group: StatementGroup | null;
  busy: boolean;
  onMove: (to: StatementMove) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const here: StatementMove | null =
    group?.kind === 'walk_in'
      ? 'walk_in'
      : group?.kind === 'bills'
        ? 'bills'
        : group?.kind === 'not_sales'
          ? 'not_sales'
          : null;
  return (
    <fieldset
      className="payments__move"
      aria-label={t('records.payments.moveTitle', { who, amount: formatPaise(entry.amount) })}
    >
      <p className="payments__move-title" aria-hidden="true">
        {t('records.payments.moveTitle', { who, amount: formatPaise(entry.amount) })}
      </p>
      {(['walk_in', 'bills', 'not_sales'] as const).map((to) => (
        <Button
          key={to}
          variant={to === here ? 'secondary' : 'primary'}
          disabled={busy || to === here}
          onClick={() => onMove(to)}
        >
          {to === here ? <Icon name="check" size={16} /> : null}
          {t(`records.payments.moveTo.${to}`)}
        </Button>
      ))}
      <Button variant="ghost" onClick={onCancel} disabled={busy}>
        {t('records.payments.moveCancel')}
      </Button>
    </fieldset>
  );
}

/** Opened from the chat's "See every payment": the sheet over it. */
export function PaymentsSheet({
  path,
  onClose,
  onFinish,
}: {
  path: string;
  onClose: () => void;
  onFinish: (how: PaymentsFinish) => void;
}) {
  const parsed = parseStatementPath(path);
  const screen = usePaymentsScreen(parsed?.importId ?? '', onFinish);
  return (
    <Sheet open onClose={onClose} label={screen.label} footer={screen.footer}>
      {screen.body}
    </Sheet>
  );
}

/** The same page on its own, for a link to it. */
export function PaymentsStandalone({
  importId,
  onFinish,
  frame,
}: {
  importId: string;
  onFinish: (how: PaymentsFinish) => void;
  frame: (screen: Screen) => ReactNode;
}) {
  const screen = usePaymentsScreen(importId, onFinish);
  return <>{frame(screen)}</>;
}
